import { Suspense, lazy, useEffect } from 'react';
import shallow from 'zustand/shallow';
import {
  useLocation,
  useNavigate,
  Routes,
  Route,
  useRoutes,
} from 'react-router-dom';

import { useAuth } from './lib/auth-provider/context';
import { useProfile } from './lib/auth-provider/context/hooks';
import Layout from './layouts/dashboard';
import { FullPageSpinner } from './common/components';
import { RoleMiddleware } from './lib/components';
import Mading from './modules/school/mading';
import Article from './modules/school/article';

const FullPageError = lazy(() => import('./common/components/full-page-error'));
const Dashboard = lazy(() => import('./modules/dashboard'));
const Home = lazy(() => import('./modules/home'));
const Settings = lazy(() => import('./modules/setting'));
const LoginModules = lazy(() => import('./modules/login'));
const ExampleModules = lazy(() => import('./modules/example'));
const SchoolScheduleModules = lazy(() => import('./modules/school/schedule'));
const PaymentHistoryModules = lazy(() => import('./modules/payment/history'));
const PaymentBillModules = lazy(() => import('./modules/payment/bill'));
const PaymentCashModules = lazy(() => import('./modules/school/finance/cash'));
const PaymentDashboardModules = lazy(() =>
  import('./modules/school/finance/dashboard'),
);
const PaymentStatusModules = lazy(() =>
  import('./modules/school/finance/status-pembayaran'),
);
const PaymentPencatatanModules = lazy(() =>
  import('./modules/school/finance/pencatatan'),
);
const PaymentLaporanModules = lazy(() =>
  import('./modules/school/finance/laporan'),
);
const PaymentWithdrawModules = lazy(() =>
  import('./modules/school/finance/withdraw'),
);
const SchoolAcademicYearModules = lazy(() =>
  import('./modules/school/academic-year'),
);
const SchoolMajorModules = lazy(() => import('./modules/school/major'));
const ReportAbsen = lazy(() => import('./modules/school/report-absen'));
const SchoolClassModules = lazy(() => import('./modules/school/class'));
const SchoolSubjectModules = lazy(() => import('./modules/school/subject'));
const SchoolAnnouncementModules = lazy(() =>
  import('./modules/school/announcement'),
);
const SchoolRoomModules = lazy(() => import('./modules/school/room'));
const SchoolAbsenceStudent = lazy(() =>
  import('./modules/school/riwayat-absen'),
);
const SchoolAbsenceTeacher = lazy(() =>
  import('./modules/school/riwayat-absen-guru'),
);
const SchoolAbsenceMatpel = lazy(() =>
  import('./modules/school/riwayat-absen-matpel'),
);
const SchoolAbsenSiswa = lazy(() => import('./modules/school/absen-siswa'));
const SchoolAbsenGuru = lazy(() => import('./modules/school/absen-guru'));

const SchoolRombelModules = lazy(() => import('./modules/school/rombel'));
const SchoolSubjectPackageModules = lazy(() =>
  import('./modules/school/subject-package'),
);
const SchoolOperatorModules = lazy(() => import('./modules/school/operator'));
const SchoolTeacherModules = lazy(() => import('./modules/school/teacher'));
const SchoolStudentModules = lazy(() => import('./modules/school/student'));
const SchoolExtraCullicular = lazy(() =>
  import('./modules/school/extracullicular'),
);
const SchoolAttendanceExtraCullicular = lazy(() =>
  import('./modules/school/riwayat-absen-eskul'),
);

const SchoolPPDB = lazy(() => import('./modules/school/ppdb'));

const Playground = lazy(() => import('./modules/playground'));

const PkbmManagementStudentsModules = lazy(() =>
  import('./modules/pkbm/manajemen-siswa'),
);

const ProgramMapel = lazy(() =>
  import('./modules/pkbm/program-mata-pelajaran'),
);

const PkbmStudentHistoryModules = lazy(() =>
  import('./modules/pkbm/riwayat-siswa'),
);

const PaymentPayrollModules = lazy(() => import('./modules/payment/payroll'));

const PaymentSchoolFeeModules = lazy(() =>
  import('./modules/payment/school-fee'),
);

const VacationModules = lazy(() => import('./modules/school/vacation'));
const StudentLeaveModules = lazy(() =>
  import('./modules/school/student-leave'),
);

const ReimbursementModules = lazy(() =>
  import('./modules/payment/reimbursement'),
);

const FinpayTransactionsModules = lazy(() =>
  import('./modules/school/finance/finpay-transactions'),
);

const DashboardKpiGuru = lazy(() => import('./modules/dashboard-kpi-guru'));

const DashboardHR = lazy(() => import('./modules/dashboard-hr'));

const DashboardTeacherAttendance = lazy(() =>
  import('./modules/dashboard-teacher-attendance'),
);

const WithLayout = ({ element }) => <Layout>{element}</Layout>;

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [getAuth] = useAuth((state) => [state.getAuth], shallow);

  const { refetch: getProfile } = useProfile();

  useEffect(() => {
    const currentUser = getAuth();
    if (location.pathname === '/' && currentUser.user?.role === 'finance') {
      navigate('/payment/dashboard');
    } else if (
      location.pathname === '/' &&
      currentUser.user?.role !== 'finance'
    ) {
      navigate('/dashboard');
    }
  }, [navigate, location.pathname, getAuth]); // Only navigate from root path

  useEffect(() => {
    const currentUser = getAuth();
    if (currentUser.token) {
      getProfile();
    }
  }, [getAuth, getProfile]);

  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        <Route path="/login/*" element={<LoginModules />} />
      </Routes>

      <Layout>
        <RoleMiddleware>
          <Routes>
            <Route path="/dashboard/*" element={<Dashboard />} />
            <Route
              path="/dashboard-teacher-attendance/*"
              element={<DashboardTeacherAttendance />}
            />
            <Route path="/home/*" element={<Home />} />
            <Route path="/example/*" element={<ExampleModules />} />
            <Route path="/settings/*" element={<Settings />} />

            <Route
              path="/school/schedule/*"
              element={<SchoolScheduleModules />}
            />
            <Route
              path="/school/report-attendance/*"
              element={<ReportAbsen />}
            />
            <Route
              path="/school/riwayat-absen-eskul/*"
              element={<SchoolAttendanceExtraCullicular />}
            />
            <Route
              path="/payment/history/*"
              element={<PaymentHistoryModules />}
            />

            <Route path="/payment/bill/*" element={<PaymentBillModules />} />
            <Route path="/payment/cash/*" element={<PaymentCashModules />} />
            <Route
              path="/payment/dashboard/*"
              element={<PaymentDashboardModules />}
            />
            <Route
              path="/school/finance/online-transactions/*"
              element={<FinpayTransactionsModules />}
            />
            <Route
              path="/payment/status-pembayaran/*"
              element={<PaymentStatusModules />}
            />
            <Route
              path="/payment/pencatatan/*"
              element={<PaymentPencatatanModules />}
            />
            <Route
              path="/payment/laporan/*"
              element={<PaymentLaporanModules />}
            />
            <Route
              path="/payment/withdraw/*"
              element={<PaymentWithdrawModules />}
            />

            <Route
              path="/school/academic-year/*"
              element={<SchoolAcademicYearModules />}
            />

            <Route path="/school/bulletin-board/*" element={<Mading />} />

            <Route path="/school/article/*" element={<Article />} />

            <Route
              path="/school/extracullicular/*"
              element={<SchoolExtraCullicular />}
            />

            <Route path="/school/ppdb/*" element={<SchoolPPDB />} />
            <Route path="/school/major/*" element={<SchoolMajorModules />} />
            <Route path="/school/class/*" element={<SchoolClassModules />} />
            <Route
              path="/school/subject/*"
              element={<SchoolSubjectModules />}
            />
            <Route path="/school/room/*" element={<SchoolRoomModules />} />
            <Route path="/school/rombel/*" element={<SchoolRombelModules />} />
            <Route
              path="/school/announcement/*"
              element={<SchoolAnnouncementModules />}
            />
            <Route
              path="/school/absen-siswa/*"
              element={<SchoolAbsenSiswa />}
            />
            <Route path="/school/absen-guru/*" element={<SchoolAbsenGuru />} />
            <Route
              path="/school/riwayat-absen/*"
              element={<SchoolAbsenceStudent />}
            />
            <Route
              path="/school/riwayat-absen-guru/*"
              element={<SchoolAbsenceTeacher />}
            />
            <Route
              path="/school/riwayat-absen-matpel/*"
              element={<SchoolAbsenceMatpel />}
            />
            <Route
              path="/school/subject-package/*"
              element={<SchoolSubjectPackageModules />}
            />
            <Route path="/playground/*" element={<Playground />} />
            <Route
              path="/school/operator/*"
              element={<SchoolOperatorModules />}
            />
            <Route
              path="/school/teacher/*"
              element={<SchoolTeacherModules />}
            />
            <Route
              path="/school/student/*"
              element={<SchoolStudentModules />}
            />
            <Route
              path="/pkbm/manajeman-siswa/*"
              element={<PkbmManagementStudentsModules />}
            />
            <Route
              path="/pkbm/program-mata-pelajaran/*"
              element={<ProgramMapel />}
            />

            <Route
              path="/pkbm/riwayat-siswa/*"
              element={<PkbmStudentHistoryModules />}
            />

            <Route
              path="/payment/payroll/*"
              element={<PaymentPayrollModules />}
            />

            <Route
              path="/payment/school-fee/*"
              element={<PaymentSchoolFeeModules />}
            />

            <Route path="/school/vacation" element={<VacationModules />} />
            <Route
              path="/school/izin-siswa/*"
              element={<StudentLeaveModules />}
            />

            <Route
              path="/payment/reimbursement"
              element={<ReimbursementModules />}
            />

            <Route
              path="/school/dashboard-kpi-guru/*"
              element={<DashboardKpiGuru />}
            />

            <Route path="/dashboard-hr/" element={<DashboardHR />} />

            <Route
              path="*"
              element={
                <FullPageError message="Page not found" errorCode={404} />
              }
            />
          </Routes>
        </RoleMiddleware>
      </Layout>
    </Suspense>
  );
}

export default App;
